// ═════════════════════════════════════════════════════════════════════════════
// Auto tab — Insurance plan (first cashless step)
//
// IPD id / payer code / policy number / description, POSTed to
// insurance/insuranceplan/request. Values are kept on the admission so
// Eligibility / Pre-Auth / Claim can reuse the policy number and insurer.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from "react";
import { C } from "../constants";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { NHCX_DUMMY_PAYER_CODE, NHCX_SANDBOX_POLICY_NO } from "../nhcx-client";
import {
  EMPTY_INSURANCE_PLAN,
  insurerKeyForPayerCode,
  readInsurancePlan,
  seedInsurancePlan,
  writeInsurancePlan,
} from "../insurance-plan";
import {
  normalizeInsurancePlanError,
  submitInsurancePlan,
} from "../insurance-plan-api";

const cardStyle = {
  background: C.card,
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
};
const innerCardStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};
const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
  lineHeight: 1.4,
  marginBottom: "6px",
};

function Field({ label, required, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>
        {label}
        {required ? " *" : ""}
      </label>
      {children}
    </div>
  );
}

export default function InsurancePlanPanel({
  admissionId,
  display,
  patient,
  onChange,
  onContinueToEligibility,
}) {
  const [plan, setPlan] = useState(EMPTY_INSURANCE_PLAN);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const userEdited = useRef(false);

  useEffect(() => {
    userEdited.current = false;
  }, [admissionId]);

  useEffect(() => {
    if (userEdited.current) return;
    const stored = readInsurancePlan(admissionId);
    if (stored?.submitted) {
      setPlan(stored);
      onChange?.(stored);
      return;
    }
    const seeded = seedInsurancePlan(display, patient);
    setPlan(seeded);
    onChange?.(seeded);
    // Seed from registration payer/policy; don't reset because `display` is a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId, display?.provider, display?.tpa, display?.policy, patient?.payer]);

  const setField = (key, value) => {
    userEdited.current = true;
    setPlan((prev) => {
      const next = { ...prev, [key]: value, submitted: false };
      if (key === "payerCode" && String(value).trim() === NHCX_DUMMY_PAYER_CODE) {
        if (!String(prev.policyNo || "").trim()) next.policyNo = NHCX_SANDBOX_POLICY_NO;
      }
      return next;
    });
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (!admissionId) {
      setError("Select a patient first.");
      return;
    }
    const payerCode = String(plan.payerCode || "").trim();
    const policyNo = String(plan.policyNo || "").trim();
    const description = String(plan.description || "").trim();
    if (!payerCode) {
      setError("Payer code is required.");
      return;
    }
    if (!policyNo) {
      setError("Policy number is required.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const body = {
        ipd_id: Number(admissionId),
        payer_code: payerCode,
        policy_number: policyNo,
      };
      if (description) body.description = description;
      await submitInsurancePlan(body);

      const next = {
        ...plan,
        payerCode,
        policyNo,
        description,
        insurerKey: insurerKeyForPayerCode(payerCode),
        submitted: true,
      };
      setPlan(next);
      writeInsurancePlan(admissionId, next);
      onChange?.(next);
    } catch (err) {
      setError(normalizeInsurancePlanError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  // Moving on without submitting still has to hand the typed payer / policy to the
  // later steps — otherwise they fall back to the values seeded from registration.
  const handleContinue = () => {
    const payerCode = String(plan.payerCode || "").trim();
    onChange?.({
      ...plan,
      payerCode,
      policyNo: String(plan.policyNo || "").trim(),
      description: String(plan.description || "").trim(),
      insurerKey: insurerKeyForPayerCode(payerCode),
    });
    onContinueToEligibility();
  };

  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="shield" size={16} color={C.primary} />
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>
            Insurance Plan · Payer details
          </h3>
        </div>
        {plan.submitted && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: "700",
              padding: "3px 9px",
              borderRadius: "20px",
              background: "#e9f7ef",
              color: C.success,
              border: "1px solid #b7e4c7",
            }}
          >
            Submitted
          </span>
        )}
      </div>

      <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
        <div style={{ fontSize: "13px", color: C.textSub }}>
          Register the payer and policy for this admission before checking eligibility.
          Later cashless steps reuse these values.
        </div>

        <div style={innerCardStyle}>
          <div style={{ fontSize: "12px", fontWeight: "800", color: C.text, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Insurance Details
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "16px",
              alignItems: "end",
            }}
          >
            <Field label="IPD ID">
              <IpdTextField
                fullWidth
                value={admissionId || ""}
                InputProps={{ readOnly: true }}
              />
            </Field>
            <Field label="Payer Code" required>
              <IpdTextField
                fullWidth
                value={plan.payerCode}
                onChange={(e) => setField("payerCode", e.target.value)}
                placeholder="e.g. 1000003538@hcx"
              />
            </Field>
            <Field label="Policy Number" required>
              <IpdTextField
                fullWidth
                value={plan.policyNo}
                onChange={(e) => setField("policyNo", e.target.value)}
                placeholder="Policy number"
              />
            </Field>
            <Field label="Description">
              <IpdTextField
                fullWidth
                value={plan.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Optional"
              />
            </Field>
          </div>
          {error && (
            <div style={{ fontSize: "13px", color: C.danger, fontWeight: "600" }}>{error}</div>
          )}
          {plan.submitted && (
            <div style={{ fontSize: "12px", color: C.textSub }}>
              IPD {admissionId} · payer {plan.payerCode} · policy {plan.policyNo}
              {plan.description ? ` · ${plan.description}` : ""}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
            <IpdButton variant="outlined" onClick={handleContinue} disabled={submitting}>
              Skip — continue to eligibility
            </IpdButton>
            <IpdButton
              onClick={handleSubmit}
              disabled={plan.submitted || submitting}
              loading={submitting}
              startIcon={<SvgIcon name="send" size={15} color="#fff" />}
            >
              {plan.submitted ? "Submitted" : "Submit"}
            </IpdButton>
          </div>
        </div>

        {plan.submitted && onContinueToEligibility && (
          <div>
            <IpdButton
              onClick={handleContinue}
              startIcon={<SvgIcon name="arrow-right" size={14} color="#fff" />}
            >
              Continue to Eligibility
            </IpdButton>
          </div>
        )}
      </div>
    </div>
  );
}
